<template>
  <div
    v-if="artOnly"
    class="playing-card art-only"
    :class="[`rarity-${rarity}`]"
    :data-rarity="rarity"
  >
    <img
      v-if="showArt"
      :src="artSrc"
      :alt="card.name"
      class="art-only-img"
      @error="artFailed = true"
    />
    <div v-else class="art-fallback">
      <i
        :class="
          isEquipment
            ? 'fas fa-shield-alt'
            : isHero || isSiege
              ? 'fas fa-user'
              : 'fas fa-clone'
        "
      ></i>
    </div>
  </div>
  <div
    v-else
    class="playing-card"
    :class="{
      'is-face-up': faceUp,
      [`rarity-${rarity}`]: true,
      'is-new': isNew,
      'lock-overlay': lockOverlay,
    }"
    :data-rarity="rarity"
  >
    <div class="card-inner">
      <div class="card-back">
        <img
          src="/assets/cards/back_playing_card_2.png"
          alt="Card back"
        />
      </div>
      <div class="card-front" :class="{ 'is-hero': isFullArt }">
        <template v-if="isFullArt">
          <div class="hero-art">
            <img
              v-if="showArt"
              :src="artSrc"
              :alt="card.name"
              class="hero-art-img"
              @error="artFailed = true"
            />
            <div v-else class="art-fallback">
              <i :class="isEquipment ? 'fas fa-shield-alt' : 'fas fa-user'"></i>
            </div>
          </div>
          <div class="hero-name-banner">
            <span class="hero-name">{{ card.name }}</span>
          </div>
          <div v-if="!hideOverlay" class="hero-type-line">
            <span class="type-kind">{{ rarityLabel }} {{ typeLabel }}</span>
            <span v-if="subType" class="type-sub">{{ subType }}</span>
          </div>
          <div v-if="!hideOverlay" class="hero-overlay">
            <div v-if="overlayStatItems.length" class="hero-stats">
              <span v-for="stat in overlayStatItems" :key="stat.key" class="hero-stat">
                <em>{{ stat.label }}</em>
                <strong>{{ stat.value }}</strong>
              </span>
            </div>
            <ul v-if="heroAbilityItems.length" class="hero-abilities">
              <li v-for="ability in heroAbilityItems" :key="ability.key">
                <span class="ability-slot">{{ ability.label }}</span>
                <span class="ability-name">{{ ability.name }}</span>
                <span v-if="ability.description" class="ability-desc">{{
                  ability.description
                }}</span>
              </li>
            </ul>
            <ul v-if="rolledAffixItems.length" class="hero-abilities">
              <li v-for="affix in rolledAffixItems" :key="affix.name">
                <span class="ability-name">{{ affix.name }}</span>
                <span v-if="affix.effect" class="ability-desc">{{ affix.effect }}</span>
              </li>
            </ul>
          </div>
        </template>
        <template v-else>
          <div class="building-art">
            <img
              v-if="artSrc"
              :src="artSrc"
              :alt="card.name"
              class="building-art-img"
            />
            <div v-else class="art-fallback">
              <i class="fas fa-clone"></i>
            </div>
          </div>
          <div class="building-body">
            <h3 class="building-name">{{ card.name }}</h3>
            <p class="building-meta">
              <span v-if="rarity">{{ rarity }}</span>
              <span v-if="card.type">{{ card.type }}</span>
              <span v-if="subType">{{ subType }}</span>
            </p>
            <p v-if="buildingStatLines.length" class="building-stats">
              <span v-for="line in buildingStatLines" :key="line">{{ line }}</span>
            </p>
            <p v-if="card.description" class="building-desc">{{ card.description }}</p>
          </div>
        </template>
        <span v-if="qty > 1" class="qty-badge">×{{ qty }}</span>
        <span v-if="isNew" class="new-badge">NEW</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { formatCardStatLines } from "../utils/cardStats";
import { abilitiesForRarity } from "../utils/heroAbilities";

const HERO_STAT_FIELDS = [
  { key: "hp", label: "HP" },
  { key: "atk", label: "ATK" },
  { key: "def", label: "DEF" },
  { key: "spd", label: "SPD" },
  { key: "crit", label: "CRIT", suffix: "%" },
];

const HERO_ABILITY_SLOTS = [
  { key: "normal", label: "Normal" },
  { key: "special_1", label: "Special 1" },
  { key: "special_2", label: "Special 2" },
  { key: "ultimate", label: "Ultimate" },
];

const props = defineProps({
  card: { type: Object, required: true },
  faceUp: { type: Boolean, default: true },
  hideOverlay: { type: Boolean, default: false },
  artOnly: { type: Boolean, default: false },
  lockOverlay: { type: Boolean, default: false },
});

const isHero = computed(() => props.card?.type === "hero");
const isEquipment = computed(() => props.card?.type === "equipment");
const isSiege = computed(() => props.card?.sub_type === "gate");
const isFullArt = computed(
  () => isHero.value || isEquipment.value || isSiege.value
);
const rarity = computed(() => props.card?.rarity || "common");
const artSrc = computed(() => props.card?.image || props.card?.image || "");
const artFailed = ref(false);
const showArt = computed(() => Boolean(artSrc.value) && !artFailed.value);
const subType = computed(() => props.card?.sub_type || props.card?.sub_type || "");
const qty = computed(() => Number(props.card?.quantity || props.card?.quantity || 1));
const isNew = computed(() => props.card?.instance_data?.is_new === true);

watch(
  () => props.card?.image,
  () => {
    artFailed.value = false;
  }
);

const overlayStatItems = computed(() => {
  const stats = props.card?.stats || {};
  const fields = isEquipment.value
    ? HERO_STAT_FIELDS.filter((field) => {
        const raw = stats[field.key];
        return raw !== undefined && raw !== null && raw !== "";
      })
    : HERO_STAT_FIELDS;
  return fields.map((field) => {
    const raw = stats[field.key];
    const value =
      raw === undefined || raw === null || raw === ""
        ? "—"
        : `${raw}${field.suffix || ""}`;
    return { ...field, value };
  });
});

const heroAbilityItems = computed(() => {
  if (!isHero.value && !isSiege.value) return [];
  const abilities = abilitiesForRarity(props.card?.abilities, rarity.value);
  return HERO_ABILITY_SLOTS.map((slot) => {
    const ability = abilities[slot.key] || {};
    return {
      key: slot.key,
      label: slot.label,
      name: ability.name || "",
      description: ability.description || "",
    };
  }).filter((item) => item.name);
});

const rolledAffixItems = computed(() => {
  if (isHero.value) return [];
  const affixes = props.card?.instance_data?.affixes || props.card?.affixes || [];
  return affixes
    .filter((affix) => affix && (affix.name || affix.key))
    .map((affix) => ({
      name: affix.name || String(affix.key).replace(/_/g, " "),
      effect:
        affix.effect ||
        affix.ability ||
        (affix.value != null && affix.value !== 0 ? String(affix.value) : ""),
    }));
});

const buildingStatLines = computed(() => formatCardStatLines(props.card));

function titleCase(value) {
  const text = String(value || "").replace(/[_-]+/g, " ").trim();
  if (!text) return "";
  return text.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

const rarityLabel = computed(() => titleCase(rarity.value));
const typeLabel = computed(() => titleCase(props.card?.type));
</script>

<style scoped>
.playing-card {
  width: 100%;
  aspect-ratio: 5 / 7;
  perspective: 1000px;
  cursor: default;
  --rarity-color: #718096;
  --hero-name-banner-height: 1.55rem;
}
.playing-card.rarity-common {
  --rarity-color: #718096;
}
.playing-card.rarity-uncommon {
  --rarity-color: #38a169;
}
.playing-card.rarity-rare {
  --rarity-color: #3182ce;
}
.playing-card.rarity-epic {
  --rarity-color: #805ad5;
}
.playing-card.rarity-legendary {
  --rarity-color: #d69e2e;
}
.card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.6s ease;
}
.playing-card.is-face-up .card-inner {
  transform: rotateY(180deg);
}
.card-front,
.card-back {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  overflow: hidden;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}
.card-back img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.card-front {
  transform: rotateY(180deg);
  background: #121820;
  border: 2px solid var(--rarity-color);
  box-shadow: 0 0 12px color-mix(in srgb, var(--rarity-color) 45%, transparent);
  color: #e7ecf1;
}

.hero-art {
  position: absolute;
  top: var(--hero-name-banner-height);
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}
.hero-art-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
}
.art-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a2332;
  color: #3d7a9e;
  font-size: 2rem;
}
.hero-name-banner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: var(--hero-name-banner-height);
  box-sizing: border-box;
  margin: 0;
  z-index: 2;
  padding: 0.2rem 0.45rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: color-mix(in srgb, var(--rarity-color) 32%, #0a0a0a);
  border: 0;
  border-bottom: 2px solid var(--rarity-color);
  border-radius: 0;
}
.hero-name {
  font-family: "Cinzel", "Palatino Linotype", serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #f8e6b0;
  text-shadow: 0 1px 2px #000;
  line-height: 1.2;
}
.hero-type-line {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 40%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  margin: 0;
  padding: 0.16rem 0.45rem;
  background: color-mix(in srgb, var(--rarity-color) 32%, #0a0a0a);
  border-top: 2px solid var(--rarity-color);
  border-bottom: 2px solid var(--rarity-color);
  font-size: 0.52rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #e7ecf1;
  line-height: 1.2;
}
.type-kind,
.type-sub {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.type-sub::before {
  content: "— ";
}
.hero-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40%;
  z-index: 2;
  padding: 0.4rem 0.5rem 0.45rem;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
  overflow-y: auto;
}
.hero-stats {
  display: flex;
  justify-content: space-between;
  gap: 0.15rem;
  flex-shrink: 0;
}
.hero-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  flex: 1;
}
.hero-stat em {
  font-style: normal;
  font-size: 0.52rem;
  letter-spacing: 0.06em;
  color: #d4af37;
  text-transform: uppercase;
}
.hero-stat strong {
  font-size: 0.72rem;
  color: #fff;
  line-height: 1.1;
}
.hero-abilities {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
}
.hero-abilities li {
  min-width: 0;
  line-height: 1.2;
}
.ability-slot {
  display: inline-block;
  margin-right: 0.3rem;
  font-size: 0.48rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #88c0d0;
}
.ability-name {
  font-size: 0.58rem;
  font-weight: 700;
  color: #fff;
}
.ability-desc {
  display: block;
  font-size: 0.5rem;
  color: #c8d4de;
  white-space: normal;
  line-height: 1.25;
}

.building-art {
  height: 52%;
  background: #0f1419;
  overflow: hidden;
}
.building-art-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
}
.building-body {
  height: 48%;
  padding: 0.45rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  overflow: hidden;
}
.building-name {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.2;
  color: #fff;
}
.building-meta {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  font-size: 0.55rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #88c0d0;
}
.building-stats {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.45rem;
  font-size: 0.62rem;
  color: #73daca;
}
.building-desc {
  margin: 0;
  font-size: 0.58rem;
  color: #aab8c5;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}
.qty-badge {
  position: absolute;
  top: 1.65rem;
  right: 6px;
  z-index: 3;
  background: rgba(0, 0, 0, 0.75);
  border: 1px solid var(--rarity-color);
  color: #f8e6b0;
  font-size: 0.65rem;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
}
.new-badge {
  position: absolute;
  top: 8px;
  left: 6px;
  z-index: 4;
  padding: 0.12rem 0.4rem;
  border-radius: 3px;
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0a0a14;
  background: linear-gradient(180deg, #fff4b0 0%, #e8d5a3 100%);
  box-shadow: 0 0 10px rgba(255, 215, 106, 0.85);
}
.playing-card.is-new .card-front {
  box-shadow:
    0 0 12px color-mix(in srgb, var(--rarity-color) 45%, transparent),
    0 0 18px rgba(255, 215, 106, 0.65),
    0 0 28px rgba(0, 246, 255, 0.35);
}

.playing-card.art-only {
  aspect-ratio: auto;
  height: 100%;
  min-height: 0;
  perspective: none;
  overflow: hidden;
  background: #121820;
}
.playing-card.art-only .art-only-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.playing-card.lock-overlay .hero-overlay,
.playing-card.lock-overlay .hero-abilities {
  overflow: hidden;
  touch-action: none;
}
</style>
